import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import AdminLayout from "../../masterLayout/AdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminDataTable from "../../components/admin/AdminDataTable";
import { adminApi, handleAdminLogoutIfNeeded } from "../../utils/adminApi";

const parseOrderData = (raw) => {
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw || {};
  } catch {
    return {};
  }
};

const isPendingOrder = (row) => {
  const mode = String(row.payment_mode || "").toLowerCase();
  if (mode === "pending") return true;
  const data = parseOrderData(row.data);
  return String(data.status || "").toLowerCase() === "pending";
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [msg, setMsg] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const res = await adminApi({ url: "admin/get-all-orders", method: "GET" });
    if (handleAdminLogoutIfNeeded(res)) return;
    if (res.success) setOrders(res.data || []);
  };

  useEffect(() => {
    document.title = "Admin Orders";
    load().catch(console.error);
  }, []);

  const activate = async (row) => {
    const data = parseOrderData(row.data);
    const planLabel = data.plan_name || `plan #${data.plan_id || "?"}`;
    if (
      !window.confirm(
        `Activate ${planLabel} for ${row.user_name || row.user_email || row.uid}?`
      )
    ) {
      return;
    }
    setBusyId(row.id);
    const res = await adminApi({
      url: "admin/activate-order",
      method: "POST",
      body: { id: row.id },
    });
    setBusyId(null);
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? "Plan activated" : "Failed"));
    if (res.success) load();
  };

  const del = async (row) => {
    if (!window.confirm("Delete this order?")) return;
    const res = await adminApi({
      url: "admin/del-order",
      method: "POST",
      body: { id: row.id },
    });
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? "Deleted" : "Failed"));
    if (res.success) load();
  };

  const columns = [
    { key: "id", label: "ID" },
    {
      key: "user",
      label: "Client",
      render: (_, row) => (
        <div style={{ minWidth: 140 }}>
          <div style={{ fontWeight: 600 }}>{row.user_name || "—"}</div>
          <div style={{ fontSize: "0.78rem", color: "#667781" }}>
            {row.user_email || row.uid}
          </div>
        </div>
      ),
    },
    {
      key: "payment_mode",
      label: "Mode",
      render: (v, row) => {
        const pending = isPendingOrder(row);
        const activated = String(v || "").toLowerCase() === "activated";
        return (
          <span
            className={`admin-chip ${pending ? "" : activated ? "" : "off"}`}
            style={
              pending
                ? { background: "#fff7ed", color: "#c2410c", borderColor: "#fdba74" }
                : activated
                  ? { background: "#ecfdf5", color: "#065f46", borderColor: "#6ee7b7" }
                  : undefined
            }
          >
            {v || "—"}
          </span>
        );
      },
    },
    {
      key: "amount",
      label: "Amount",
      render: (v) => <strong>{Number(v || 0).toLocaleString()}</strong>,
    },
    {
      key: "data",
      label: "Plan",
      render: (v) => {
        const data = parseOrderData(v);
        const label = data.plan_name
          ? `${data.plan_name}${data.plan_id ? ` (#${data.plan_id})` : ""}`
          : typeof v === "string"
            ? v
            : JSON.stringify(v || "");
        return (
          <span
            style={{ maxWidth: 220, display: "inline-block" }}
            className="text-truncate"
            title={label}
          >
            {label}
          </span>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="d-flex gap-2">
          {isPendingOrder(row) ? (
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              style={{ minHeight: 34 }}
              disabled={busyId === row.id}
              onClick={() => activate(row)}
              title="Activate plan for this user"
            >
              <Icon icon="mdi:check-decagram" width={16} />
              {busyId === row.id ? "…" : "Activate"}
            </button>
          ) : null}
          <button
            type="button"
            className="admin-btn admin-btn-danger"
            style={{ minHeight: 34 }}
            onClick={() => del(row)}
            title="Delete order"
          >
            <Icon icon="lucide:trash-2" width={16} />
          </button>
        </div>
      ),
    },
  ];

  const pendingCount = orders.filter(isPendingOrder).length;

  return (
    <AdminLayout>
      <div className="admin-ui">
        <AdminPageHeader
          title="Orders"
          subtitle={`${orders.length} orders · ${pendingCount} pending activation. Use Activate after payment is confirmed.`}
        />
        {msg ? <div className="admin-alert admin-alert-info">{msg}</div> : null}
        <AdminDataTable columns={columns} rows={orders} />
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
